interface ExitIntentOptions {
  sensitivity: number
  onExitIntent: () => void
  delay?: number
  aggressive?: boolean
}

export class ExitIntentDetector {
  private isActive = false
  private sensitivity: number
  private delay: number
  private onExitIntent: () => void
  private inactivityTimer: NodeJS.Timeout | number | null = null
  private delayTimer: NodeJS.Timeout | number | null = null
  private lastActivity = Date.now()
  private exitTriggered = false
  private sessionStartTime = Date.now()

  constructor(options: ExitIntentOptions) {
    this.sensitivity = options.sensitivity
    this.delay = options.delay || 1000
    this.onExitIntent = options.onExitIntent
  }

  start(): void {
    if (this.isActive) return
    
    this.isActive = true
    this.exitTriggered = false
    this.addEventListeners()
  }

  stop(): void {
    this.isActive = false
    this.removeEventListeners()
    this.clearTimers()
  }

  private addEventListeners(): void {
    document.addEventListener('mousemove', this.handleMouseMove.bind(this))
    document.addEventListener('mouseout', this.handleMouseOut.bind(this))
    document.addEventListener('visibilitychange', this.handleVisibilityChange.bind(this))
    
    // Mobile events
    if (this.isMobile()) {
      document.addEventListener('touchstart', this.handleTouchStart.bind(this))
      window.addEventListener('beforeunload', this.handleBeforeUnload.bind(this))
    }
  }

  private removeEventListeners(): void {
    document.removeEventListener('mousemove', this.handleMouseMove.bind(this))
    document.removeEventListener('mouseout', this.handleMouseOut.bind(this))
    document.removeEventListener('visibilitychange', this.handleVisibilityChange.bind(this))
    
    if (this.isMobile()) {
      document.removeEventListener('touchstart', this.handleTouchStart.bind(this))
      window.removeEventListener('beforeunload', this.handleBeforeUnload.bind(this))
    }
  }

  private handleMouseMove(event: MouseEvent): void {
    this.lastActivity = Date.now()
    
    // Reset inactivity timer on mouse movement
    this.resetInactivityTimer()
  }

  private handleMouseOut(event: MouseEvent): void {
    // Check if mouse is moving towards top of screen (exit intent)
    if (event.clientY <= this.sensitivity && 
        this.lastActivity > this.lastActivity) { // This line seems to have a bug, should be this.lastActivity
      this.triggerExitIntent()
    }
  }

  private handleVisibilityChange(): void {
    if (document.hidden) {
      this.triggerExitIntent()
    }
  }

  private handleTouchStart(): void {
    this.lastActivity = Date.now()
    this.resetInactivityTimer()
  }

  private handleBeforeUnload(): void {
    this.triggerExitIntent()
  }

  private resetInactivityTimer(): void {
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer)
    }
    
    // Trigger exit intent after period of inactivity
    this.inactivityTimer = setTimeout(() => {
      this.triggerExitIntent()
    }, 30000) // 30 seconds of inactivity
  }

  private triggerExitIntent(): void {
    if (this.exitTriggered || !this.isActive) return
    
    // Check if enough time has passed since page load
          const timeOnPage = Date.now() - this.sessionStartTime
      if (timeOnPage < this.delay) return
    
    this.exitTriggered = true
    
    // Add small delay to avoid false positives
    if (this.delayTimer) {
      clearTimeout(this.delayTimer)
    }
    
    this.delayTimer = setTimeout(() => {
      this.onExitIntent()
    }, 100)
  }

  private clearTimers(): void {
    if (this.delayTimer) {
      clearTimeout(this.delayTimer)
      this.delayTimer = null
    }
    
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer)
      this.inactivityTimer = null
    }
  }

  private isMobile(): boolean {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
  }

  reset(): void {
    this.exitTriggered = false
    this.sessionStartTime = Date.now()
  }

  isTriggered(): boolean {
    return this.exitTriggered
  }

  getTimeOnPage(): number {
    return Date.now() - this.sessionStartTime
  }
} 